
import {
  Component, Emit, InjectReactive, Prop, Vue, Watch,
} from 'nuxt-property-decorator'
import { ProjectProperties } from 'fsxa-api'
import VueUid from 'vue-uid'
import { TIcon } from '../../../shared/general/types/TIcon'
import { trackCta } from '../../../shared/general/tracking/TrackingService'
import { scrollToSection } from '../../../shared/general/services/AnchorLinkService'
import { TLocationCta } from '../../../shared/general/types/TLocationCta'

Vue.use(VueUid)

@Component({
  name: 'BaseToolbarElement',
  components: {
    BaseLink: () => import('../BaseLink.vue'),
    BaseIcon: () => import('../BaseIcon.vue'),
    BaseTooltip: () => import('../BaseTooltip.vue'),
  },
})
export default class BaseToolbarElement extends Vue {
  @InjectReactive({ from: 'globalSettings' }) globalSettings! : ProjectProperties | null

  @Prop({ required: true }) iconName! : string

  @Prop({ default: 'light' }) iconType! : TIcon

  @Prop({ default: false }) isBookmarkable! : boolean

  @Prop() text ?: string

  @Prop() href ?: string

  @Prop() target ?: boolean

  @Prop({ default: false }) isSidebar! : boolean

  @Prop() sidebarComponent ?: string

  @Prop() customEventName ?: string

  @Prop() tooltipText ?: string

  private hasBookmark : boolean = false

  private active : boolean = false

  private isToolTipActive : boolean = false

  private timeout : ReturnType<typeof setTimeout> = setTimeout(() => '', 0)

  private get sidebarOpen () : boolean {
    return !!this.$store.state.Sidebar.open
  }

  private get tag () : 'a' | 'button' | 'BaseLink' {
    if (this.href && !this.target) return 'BaseLink'
    return this.href ? 'a' : 'button'
  }

  private get props () : Record<string, string | boolean | undefined> {
    return {
      href: this.href,
      target: this.target ? '_blank' : '_self',
      url: this.href && !this.target ? this.href : false,
      onlySlotContent: this.tag === 'BaseLink',
    }
  }

  public reset () : void {
    this.active = false
  }

  private initTooltip () : void {
    this.timeout = setTimeout(this.showTooltip, 500)
  }

  private showTooltip () : void {
    this.isToolTipActive = true
  }

  private discardTooltip () : void {
    clearTimeout(this.timeout)
    this.isToolTipActive = false
  }

  private toggleSidebar () : void {
    this.$store.commit('Sidebar/set', !this.$store.state.Sidebar.open)
  }

  private track () : void {
    trackCta(this.$el as HTMLElement, {
      link_text: this.text || '',
      link_url: this.href || '',
      icon_name: this.iconName,
      cta_type: 'cta_toolbar',
    })
  }

  @Emit('click')
  private click () : boolean {
    this.track()

    if (!this.href) {
      if (this.isSidebar) {
        this.active = true
        this.$store.commit('SidebarContent/setContent', {
          component: this.sidebarComponent,
          props: {
            view: this.$store.state.ToolbarElements.locationSidebarView,
            locationCta: this.locationCta,
            locationTypes: [],
            inSidebar: true,
            id: this.$_uid,
            preFilters: this.$store.state.ToolbarElements.locationSidebarType ? [this.$store.state.ToolbarElements.locationSidebarType] : [],
            tagFilters: [],
          },
        })
        this.toggleSidebar()
      } else if (this.customEventName) window.dispatchEvent(new Event(this.customEventName))
    }

    scrollToSection(this.href?.substring(this.href.indexOf('#')) || '')

    return true
  }

  private get locationCta () : TLocationCta {
    const countryProjectSettings = this.globalSettings?.data?.ps_country_project_settings || {}
    return {
      primary: countryProjectSettings.pt_primary_cta,
      secondary: countryProjectSettings.pt_other_cta_1,
      tertiary: countryProjectSettings.pt_other_cta_2,
    }
  }

  @Watch('$store.state.Locations.savedLocations', { deep: true, immediate: true })
  savedLocationsChange (locationTypes : Record<string, string>) : void {
    this.hasBookmark = Object.keys(locationTypes).includes(this.$store.state.ToolbarElements.locationSidebarType?.value)
  }
}
